/**
 * Negotiation API Service
 *
 * Task 302: API methods for negotiation findings CRUD.
 * Backend endpoints defined in backend/app/routers/negotiation.py
 */

import { apiClient } from './client';

export interface NegotiationFinding {
  id: string;
  analysis_run_id: string;
  document_id: string;
  clause_id: string | null;
  finding_type: string; // 'negotiated_position' | 'untouched_opportunity'
  finding_subtypes: string[]; // ['change_analysis', 'comment_analysis', 'red_flag']
  title: string | null;
  risk_level: string; // 'high' | 'medium' | 'low' (Plan 58 — legacy 'critical' folded via normalizeSeverity)
  change_summary: string | null;
  comment_summary: string | null;
  risk_analysis: string | null;
  recommendation: string | null;
  recommendation_strength: string | null;
  verbatim_comments?: { author: string | null; text: string; anchor_text: string | null }[];
  agent_context: Record<string, any> | null;
  // Plan 113: AI reasoning transparency (nested in agent_context for negotiation findings)
  reasoning?: string | null;
  evidence?: {
    primary?: { logical_id?: string; heading?: string };
    neighbors?: Array<{ logical_id?: string; heading?: string }>;
    cross_refs?: Array<{ logical_id?: string; heading?: string }>;
    doc_type?: string;
    negotiation?: {
      tracked_changes_count?: number;
      comments_count?: number;
      user_side?: string;
      tools_called?: string[];
      agent_turns?: number;
    };
    scope?: {
      has_neighbors?: boolean;
      has_cross_refs?: boolean;
      has_doc_type?: boolean;
      has_tracked_changes?: boolean;
      has_comments?: boolean;
      is_full_document?: boolean;
    };
  } | null;
  status: string; // 'pending' | 'reviewed' | 'drafted' | 'accepted'
  created_at: string;
  // Plan 180: per-finding soft delete metadata. Distinct from status — dismiss
  // means "hide from default view", not a workflow-progression transition.
  dismissed_at?: string | null;
  dismissed_by?: string | null;
  // Plan 196: playbook provenance. Both null when the agent didn't declare
  // matched_playbook_(clause_type, tier) or when the declaration didn't
  // resolve to a real position. Agent_context also carries
  // matched_playbook_tier and matched_playbook_clause_type as a
  // render-time convenience (avoids the FE needing to join through
  // playbook_positions to display the tier chip).
  source_playbook_id?: string | null;
  source_position_id?: string | null;
}

export interface NegotiationFindingsResponse {
  findings: NegotiationFinding[];
  /** Plan 180: total dismissed findings on this analysis run. */
  dismissed_count?: number;
}

export interface NegotiationDraftResult {
  clause_id: string;
  cost: number;
  turns: number;
  tool_calls_count: number;
  has_revision: boolean;
  has_comment: boolean;
  comment_text?: string | null;
  comment_audience?: string | null;
  baseline_text?: string | null;
  proposed_text?: string | null;
  proximity_warning?: boolean;
  stop_reason: string | null;
  result: string | null;
}

export interface NegotiationDraftResponse {
  finding_id: string;
  status: string;
  draft_result: NegotiationDraftResult;
}

export interface NegotiationConfigPayload {
  document_id: string;
  side: 'customer' | 'vendor';
  areas_of_concern?: string;
  review_untouched_areas?: boolean;
  playbook_id?: string;  // Plan 196: optional per-run playbook
}

// Change manifest types consolidated into clauseRevisionApi.ts (Issue #496)
// Use ChangeManifestResponse from clauseRevisionApi instead.

export interface ClauseNegotiationState {
  id: string;
  analysis_run_id: string;
  clause_id: string;
  status: 'pending' | 'reviewed' | 'drafted' | 'accepted' | 'skipped';
  selected_revision_id: string | null;
  selected_strength: string | null;
  view_default: 'incoming' | 'response';
  o_text: string;
  c_text: string;
  clause_fingerprint: string | null;
  proposed_text: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClauseStatesResponse {
  clause_states: ClauseNegotiationState[];
}

export interface RedlineSegment {
  type: 'equal' | 'delete' | 'insert';
  text: string;
}

export interface ClauseRedlineData {
  status: 'ok' | 'fallback';
  segments: RedlineSegment[];
}

export interface RedlineSegmentsResponse {
  segments_by_clause: Record<string, ClauseRedlineData>;
  /**
   * Plan 48 / #854: the document's redline diff exceeded the server LCS cap
   * and was skipped to avoid an OOM. The doc HAS tracked changes; the inline
   * word-diff just can't be rendered. Distinct from "no tracked changes"
   * (which is an empty segments_by_clause with no flag).
   */
  too_large?: boolean;
  /**
   * Plan 78 / #1095: the document HAS tracked changes, but their per-clause
   * `original_text` baselines are missing, so there is nothing to diff against.
   * Set on the PARTIAL case too — two of the five affected documents kept one
   * surviving baseline, so "some clauses render" does not mean healthy.
   * Distinct from "no tracked changes" (an empty segments_by_clause, no flag).
   */
  baseline_unavailable?: boolean;
}

export const negotiationApi = {
  configureNegotiation: (analysisRunId: string, config: NegotiationConfigPayload) =>
    apiClient.post(`/api/v1/analysis-runs/${analysisRunId}/configure/negotiation`, config),

  getFindings: (
    analysisRunId: string,
    filtersOrIncludeDismissed?: { risk_level?: string; finding_type?: string; includeDismissed?: boolean } | boolean,
  ) => {
    // Backward-compat: the second arg used to be only a filters object. Plan 180
    // adds `includeDismissed` so the call sites that pass a plain boolean also
    // work (used by useNegotiationFindingsQuery).
    const filters =
      typeof filtersOrIncludeDismissed === 'boolean'
        ? { includeDismissed: filtersOrIncludeDismissed }
        : filtersOrIncludeDismissed;
    const params = new URLSearchParams();
    if (filters?.risk_level) params.set('risk_level', filters.risk_level);
    if (filters?.finding_type) params.set('finding_type', filters.finding_type);
    if (filters?.includeDismissed) params.set('include_dismissed', 'true');
    const qs = params.toString();
    return apiClient.get<NegotiationFindingsResponse>(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/findings${qs ? `?${qs}` : ''}`
    );
  },

  getFinding: (analysisRunId: string, findingId: string) =>
    apiClient.get<NegotiationFinding>(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/findings/${findingId}`
    ),

  getFindingRevisions: (analysisRunId: string, findingId: string) =>
    apiClient.get<{ baseline_text: string | null; proposed_text: string | null; comment_text: string | null; comment_audience: string | null }>(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/findings/${findingId}/revision`
    ),

  updateFinding: (analysisRunId: string, findingId: string, data: Partial<Pick<NegotiationFinding, 'status' | 'recommendation_strength'>>) =>
    apiClient.put<NegotiationFinding>(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/findings/${findingId}`,
      data
    ),

  /** Plan 180: soft-delete a single negotiation finding. */
  dismissFinding: (analysisRunId: string, findingId: string) =>
    apiClient.patch<void>(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/findings/${findingId}/dismiss`,
      {},
    ),

  /** Plan 180: undo a previous dismiss. */
  restoreFinding: (analysisRunId: string, findingId: string) =>
    apiClient.patch<void>(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/findings/${findingId}/restore`,
      {},
    ),

  getSummary: (analysisRunId: string) =>
    apiClient.get(`/api/v1/analysis-runs/${analysisRunId}/negotiation/summary`),

  draftResponse: (
    analysisRunId: string,
    findingId: string,
    data: { response_strength: string; comment_audience: string; refinement_prompt?: string }
  ) =>
    apiClient.post<NegotiationDraftResponse>(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/findings/${findingId}/draft`,
      data,
      { timeout: 120000 }
    ),

  runAnalysis: (analysisRunId: string) =>
    apiClient.post(`/api/v1/analysis-runs/${analysisRunId}/negotiation/run`),

  // getChangeManifest removed (Issue #496) — use clauseRevisionApi.getChangeManifest(documentId, analysisRunId)

  getRedlineSegments: (analysisRunId: string, documentId: string) =>
    apiClient.get<RedlineSegmentsResponse>(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/redline-segments?document_id=${encodeURIComponent(documentId)}`
    ),

  getClauseStates: (analysisRunId: string) =>
    apiClient.get<ClauseStatesResponse>(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/clause-states`
    ),

  updateClauseState: (
    analysisRunId: string,
    clauseId: string,
    data: { status?: string; view_default?: string }
  ) =>
    apiClient.put(
      `/api/v1/analysis-runs/${analysisRunId}/negotiation/clause-states/${encodeURIComponent(clauseId)}`,
      data
    ),
};
